import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import './Banned.css';

function parseBanInfo(raw) {
  try {
    const obj = JSON.parse(decodeURIComponent(raw));
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return null;
    return {
      reason: typeof obj.reason === 'string' ? obj.reason.slice(0, 500) : null,
      banned_at: typeof obj.banned_at === 'string' ? obj.banned_at : null,
      expires_at: typeof obj.expires_at === 'string' ? obj.expires_at : null,
      is_permanent: typeof obj.is_permanent === 'boolean' ? obj.is_permanent : (typeof obj.is_permanent === 'number' ? !!obj.is_permanent : true),
      banned_by: typeof obj.banned_by === 'string' ? obj.banned_by.slice(0, 100) : null,
    };
  } catch { return null; }
}

const Banned = ({ banInfo: propBanInfo }) => {
  const { banInfo: contextBanInfo, setBanFromUrl } = useAuth();
  const location = useLocation();
  const [banInfo, setBanInfo] = useState(propBanInfo || contextBanInfo);
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const banInfoParam = urlParams.get('ban') || urlParams.get('info');

    if (banInfoParam && !banInfo) {
      const parsed = parseBanInfo(banInfoParam);
      if (parsed) {
        setBanInfo(parsed);
        if (setBanFromUrl) {
          setBanFromUrl(banInfoParam);
        }
      }
    }
  }, [location, banInfo, setBanFromUrl]);

  const defaultBanInfo = useMemo(() => ({
    reason: 'Account suspended',
    banned_at: new Date().toISOString(),
    expires_at: null,
    is_permanent: 1,
    banned_by: 'Admin'
  }), []);

  const displayBanInfo = banInfo || defaultBanInfo;

  const isPermanent = displayBanInfo.is_permanent || !displayBanInfo.expires_at;
  const expiresAtMs = displayBanInfo.expires_at ? new Date(displayBanInfo.expires_at).getTime() : null;
  const isExpired = !isPermanent && expiresAtMs && expiresAtMs <= now;

  // Tick every second only when there's an active countdown
  useEffect(() => {
    if (isPermanent || isExpired) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isPermanent, isExpired]);

  const statusType = isExpired ? 'expired' : isPermanent ? 'permanent' : 'temporary';
  const statusLabel = isExpired ? 'Ban Expired' : isPermanent ? 'Permanent Ban' : 'Temporary Restriction';

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const countdown = useMemo(() => {
    if (isPermanent || !expiresAtMs) return null;
    const diff = Math.max(0, expiresAtMs - now);
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, totalMs: diff };
  }, [expiresAtMs, isPermanent, now]);

  // Deterministic short reference from banned_at + reason, so users can quote it to support.
  const banReference = useMemo(() => {
    const seed = `${displayBanInfo.banned_at || ''}::${displayBanInfo.reason || ''}`;
    let hash = 5381;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) + hash + seed.charCodeAt(i)) >>> 0;
    }
    return `GVS-${hash.toString(36).toUpperCase().padStart(6, '0').slice(0, 8)}`;
  }, [displayBanInfo.banned_at, displayBanInfo.reason]);

  const handleCopyRef = async () => {
    try {
      await navigator.clipboard.writeText(banReference);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      // no-op
    }
  };

  return (
    <div className="banned-page" data-status={statusType}>
      <div className="banned-bg-glow glow-red" aria-hidden="true"></div>
      <div className="banned-bg-glow glow-purple" aria-hidden="true"></div>
      <div className="banned-grid" aria-hidden="true"></div>
      <div className="banned-noise" aria-hidden="true"></div>

      <div className="banned-container">

        <Link to="/" className="banned-logo">
          <Logo className="banned-logo-icon" />
          <span>GV-Security</span>
        </Link>

        <div className={`ban-status-pill ban-status-${statusType}`}>
          <span className="ban-status-dot"></span>
          {statusLabel}
        </div>

        <div className={`ban-icon-wrap ban-icon-${statusType}`}>
          <div className="ban-icon-ring ban-ring-1"></div>
          <div className="ban-icon-ring ban-ring-2"></div>
          <div className="ban-icon-ring ban-ring-3"></div>
          <div className="ban-icon-inner">
            {isExpired ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
              </svg>
            )}
          </div>
        </div>

        <h1 className="ban-title">
          {isExpired ? 'Welcome back' : 'Account Suspended'}
        </h1>
        <p className="ban-subtitle">
          {isExpired
            ? 'Your restriction period has ended. You can return to GV-Security at any time.'
            : isPermanent
            ? 'Access to this account has been permanently revoked.'
            : 'Your account is temporarily restricted. Access will be restored automatically.'}
        </p>

        {!isPermanent && !isExpired && countdown && (
          <div className="ban-countdown" aria-live="polite">
            <div className="ban-countdown-label">Time remaining</div>
            <div className="ban-countdown-grid">
              <div className="ban-countdown-unit">
                <span className="ban-countdown-num">{String(countdown.days).padStart(2, '0')}</span>
                <span className="ban-countdown-tag">days</span>
              </div>
              <span className="ban-countdown-sep">:</span>
              <div className="ban-countdown-unit">
                <span className="ban-countdown-num">{String(countdown.hours).padStart(2, '0')}</span>
                <span className="ban-countdown-tag">hrs</span>
              </div>
              <span className="ban-countdown-sep">:</span>
              <div className="ban-countdown-unit">
                <span className="ban-countdown-num">{String(countdown.minutes).padStart(2, '0')}</span>
                <span className="ban-countdown-tag">min</span>
              </div>
              <span className="ban-countdown-sep">:</span>
              <div className="ban-countdown-unit">
                <span className="ban-countdown-num">{String(countdown.seconds).padStart(2, '0')}</span>
                <span className="ban-countdown-tag">sec</span>
              </div>
            </div>
          </div>
        )}

        <div className="ban-card">
          <div className="ban-card-header">
            <span>Ban Details</span>
            <button
              type="button"
              className={`ban-ref ${copied ? 'is-copied' : ''}`}
              onClick={handleCopyRef}
              title="Copy reference code"
            >
              <span className="ban-ref-code">{banReference}</span>
              {copied ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              )}
            </button>
          </div>
          <div className="ban-card-body">
            <div className="ban-row">
              <span className="ban-row-label">Reason</span>
              <span className="ban-row-value">{displayBanInfo.reason || 'No reason provided'}</span>
            </div>
            <div className="ban-row">
              <span className="ban-row-label">Banned on</span>
              <span className="ban-row-value">{formatDate(displayBanInfo.banned_at)}</span>
            </div>
            <div className="ban-row">
              <span className="ban-row-label">Duration</span>
              <span className={`ban-row-value ban-duration-${statusType}`}>
                {isPermanent
                  ? 'Permanent'
                  : isExpired
                  ? `Expired ${formatDate(displayBanInfo.expires_at)}`
                  : `Until ${formatDate(displayBanInfo.expires_at)}`}
              </span>
            </div>
            {displayBanInfo.banned_by && (
              <div className="ban-row">
                <span className="ban-row-label">Issued by</span>
                <span className="ban-row-value">Admin</span>
              </div>
            )}
          </div>
        </div>

        <div className="ban-actions">
          {isExpired ? (
            <Link to="/login" className="ban-btn ban-btn-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                <polyline points="10 17 15 12 10 7"/>
                <line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
              Sign back in
            </Link>
          ) : (
            <Link to="/" className="ban-btn ban-btn-ghost">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              Return home
            </Link>
          )}
          <Link to="/support" className="ban-btn ban-btn-outline">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Appeal this decision
          </Link>
        </div>

        <p className="ban-footnote">
          Quote reference <span className="ban-footnote-code">{banReference}</span> when contacting support.
        </p>

      </div>
    </div>
  );
};

export default Banned;
